import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGSAP } from '@/hooks/useGSAP';

import type { Case } from '@lnr-v2/types';
import { CaseCard } from '../CaseCard';

import styles from './CasesCarousel.module.css';
import { useCursor } from '@/contexts/CursorContext';

interface CasesCarouselProps {
  cases: Case[];
  className?: string;
}

export const CasesCarousel: React.FC<CasesCarouselProps> = ({
  cases,
  className,
}) => {
  const { setState } = useCursor();
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { gsap, ScrollTrigger, ready } = useGSAP();

  const handleClickNavigation = () => {
    navigate('/cases/' + cases[activeIndex].slug);
  };

  // Scroll animation
  useEffect(() => {
    if (!ready || !gsap || !ScrollTrigger) return;
    if (!containerRef.current) return;

    // Petit délai pour s'assurer que le DOM est prêt
    const triggers: InstanceType<typeof ScrollTrigger>[] = [];
    const timeout = setTimeout(() => {
      const slides = gsap.utils.toArray<HTMLElement>(
        '.slide-showreel',
        containerRef.current!
      );

      if (slides.length === 0) return;

      slides.forEach((slide, index) => {
        const isLast = index === slides.length - 1;
        const nextSlide = slides[index + 1];

        // Reset initial state
        gsap.set(slide, {
          scale: 1,
          filter: 'blur(0px)',
          clearProps: 'opacity',
          force3D: true,
        });

        // Ne pas animer la dernière slide
        if (isLast) {
          const trigger = ScrollTrigger.create({
            trigger: slide,
            start: 'top 80%',
            end: 'top 20%',
            scrub: 1,
            invalidateOnRefresh: true,
            onEnter: () => setActiveIndex(index),
            onEnterBack: () => setActiveIndex(index),
          });
          triggers.push(trigger);
          return;
        }

        // Animation de scale et blur (sans opacity)
        // Reduced blur amount for better performance
        const tween = gsap.to(slide, {
          scale: 0.92,
          filter: 'blur(4px)', // Reduced from 6px
          ease: 'none',
          scrollTrigger: {
            trigger: nextSlide,
            start: 'top 80%',
            end: 'top 20%',
            scrub: 1, // Slightly slower scrub for smoother performance
            invalidateOnRefresh: true,
          },
        });

        if (tween.scrollTrigger) {
          triggers.push(
            tween.scrollTrigger as InstanceType<typeof ScrollTrigger>
          );
        }

        // Track active index
        const indexTrigger = ScrollTrigger.create({
          trigger: slide,
          start: 'top center',
          onEnter: () => setActiveIndex(index),
          onEnterBack: () => setActiveIndex(index),
        });
        triggers.push(indexTrigger);
      });

      ScrollTrigger.refresh();
    }, 100);

    return () => {
      clearTimeout(timeout);
      triggers.forEach((trigger) => trigger.kill());
    };
  }, [cases, ready, gsap, ScrollTrigger]);

  if (cases.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.carousel} ${className || ''}`}>
      <div
        className={styles.carouselContent}
        onMouseEnter={() => setState('other')}
        onMouseLeave={() => setState('default')}
        ref={containerRef}
        onClick={() => handleClickNavigation()}
      >
        <div className={styles.sliderLayout}>
          {cases.map((caseItem) => (
            <div
              key={caseItem.id}
              className={`${styles.slideWrapper} slide-showreel`}
            >
              <div className={styles.slide}>
                <CaseCard case={caseItem} isActive={true} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
